/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { addHours, format, parseISO } from 'date-fns';

import api from '~/services/api';

import { Container, Filter } from './styles';
import InfiniteScroll from '~/components/InfiniteScroll';
import BtnNewEvent from '~/components/BtnNewEvent';
import { IOption } from '~/components/Select';
import FiltersLists from '~/components/FiltersLists';

import imgDefault from '~/assets/icons/image-default.svg';
import Loading from '~/components/Loading';
import Card from './Card';
import formatDate from '~/utils/formatDate';

export interface IEvent {
  id: number;
  archive_url: string;
  title: string;
  date: string;
  type: string;
  type_image_url: string;
  status: string;
  unlisted: boolean;
  endRegistration: string;
  purchases: number;
  allTickets: number;
  slug: string;
  analytics?: string;
  facebook?: string;
  addKart?: boolean;
  startPayment?: boolean;
  checkout?: boolean;
  has_marketing?: boolean;
}

export interface IEventData {
  id: number;
  event_archive_url: string;
  name: string;
  type_name: string;
  type_archive_url: string;
  date: string;
  status_name: string;
  sales_end_date: string;
  qty_sold: string;
  qty_cortesies: string;
  total: string;
  unlisted: boolean;
  has_marketing: boolean;
  slug: string;
}

interface IEventResponse {
  data: IEventData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IEventType {
  id: number;
  name: string;
}

interface IStatus {
  id: number;
  name: string;
}

const Events: React.FC = () => {
  const [events, setEvents] = useState<IEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState<IOption[]>([]);
  const [status, setStatus] = useState<IOption[]>([]);
  const [statusSelected, setStatusSelected] = useState<string | undefined>(
    undefined
  );
  const [typeSelected, setTypeSelected] = useState<string | undefined>(
    undefined
  );
  const [dateStartSelected, setDateStartSelected] = useState<
    string | undefined
  >(undefined);
  const [dateEndSelected, setDateEndSelected] = useState<string | undefined>(
    undefined
  );

  const [totalEvents, setTotalEvents] = useState(0);
  const [eventIdData, setEventIdData] = useState<number | undefined>(undefined);

  useEffect(() => {
    api.get<IStatus[]>('events-status').then((response) => {
      const data = response.data.map<IOption>((statusData) => ({
        id: statusData.id,
        value: statusData.name,
        selected: false,
      }));
      setStatus(data);
    });
  }, []);

  const handleLoadEvents = useCallback(
    async (
      pageData,
      status_id?: string,
      type_id?: string,
      dateStart?: string,
      dateEnd?: string,
      eventId?: number
    ) => {
      try {
        setLoadingMore(true);
        const response = await api.get<IEventResponse>('events/organizer', {
          params: {
            page: pageData,
            category_id: type_id,
            status_ids: status_id
              ? [status_id]
              : ['0', '1', '2', '3', '4', '5', '9'],
            startDate: dateStart ? new Date(dateStart) : undefined,
            endDate: dateEnd ? new Date(dateEnd) : undefined,
            event_id: eventId,
          },
        });

        const data = response.data.data.map<IEvent>((event) => {
          const allTickets = event.total ? parseInt(event.total, 10) : 0;

          const endRegistration = formatDate(
            event.sales_end_date || event.date,
            'dd/MM/yyyy'
          );

          const purchases = event.qty_sold ? parseInt(event.qty_sold, 10) : 0;
          const courtesies = event.qty_cortesies
            ? parseInt(event.qty_cortesies, 10)
            : 0;

          return {
            id: event.id,
            archive_url: event.event_archive_url || imgDefault,
            title: event.name,
            type: event.type_name,
            type_image_url: event.type_archive_url,
            date: format(addHours(parseISO(event.date), 3), 'dd/MM/yyyy'),
            status: event.status_name || 'Cadastrando',
            unlisted: event.unlisted,
            endRegistration,
            purchases: purchases + courtesies,
            allTickets,
            slug: event.slug,
            has_marketing: event.has_marketing,
          };
        });

        if (pageData === 1) {
          setEvents(data);
        } else {
          setEvents((oldState) => [...oldState, ...data]);
        }
        setTotalPages(response.data.pages);
        setTotalEvents(response.data.total);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    handleLoadEvents(1);
  }, [handleLoadEvents]);

  useEffect(() => {
    api
      .get<IEventType[]>('events-types', { params: { isOrganizer: true } })
      .then((response) => {
        const data = response.data.map<IOption>((modality) => ({
          id: modality.id,
          value: modality.name,
          selected: false,
        }));

        setCategories(data);
      });
  }, []);

  const handleClickDelete = useCallback((event) => {
    setEvents((oldState) =>
      oldState.filter((eventData) => eventData.id !== event.id)
    );
  }, []);

  const handleCopy = useCallback((event: IEventData) => {
    const allTickets = event.total ? parseInt(event.total, 10) : 0;

    const endRegistration = formatDate(
      event.sales_end_date || event.date,
      'dd/MM/yyyy'
    );

    const purchases = 0;

    const newEvent: IEvent = {
      id: event.id,
      archive_url: event.event_archive_url || imgDefault,
      title: event.name,
      type: event.type_name,
      type_image_url: event.type_archive_url,
      date: format(addHours(parseISO(event.date), 3), 'dd/MM/yyyy'),
      status: event.status_name || 'Cadastrando',
      unlisted: event.unlisted,
      endRegistration,
      purchases,
      allTickets,
      slug: event.slug,
    };
    //
    setEvents((oldState) => [newEvent, ...oldState]);
  }, []);

  const handleLoad = useCallback(async () => {
    if (!loadingMore) {
      setLoadingMore(true);
      if (page < totalPages) {
        await handleLoadEvents(
          page + 1,
          statusSelected !== 'todos' ? statusSelected : undefined,
          typeSelected !== 'todos' ? typeSelected : undefined,
          dateStartSelected,
          dateEndSelected,
          eventIdData
        );
        setPage(page + 1);
      } else {
        setLoadingMore(false);
      }
    }
  }, [
    loadingMore,
    page,
    totalPages,
    handleLoadEvents,
    statusSelected,
    typeSelected,
    dateStartSelected,
    dateEndSelected,
    eventIdData,
  ]);

  const handleSubmitFilters = useCallback(
    (data) => {
      setEventIdData(
        data.event_id && data.event_id !== '-1'
          ? parseInt(data.event_id, 10)
          : undefined
      );
      setStatusSelected(data.status);
      setTypeSelected(data.sports);
      setDateStartSelected(data.dateStart);
      setDateEndSelected(data.dateEnd);

      setPage(1);
      handleLoadEvents(
        1,
        data.status !== 'todos' ? data.status : undefined,
        data.sports !== 'todos' ? data.sports : undefined,
        data.dateStart,
        data.dateEnd,
        data.event_id && data.event_id !== '-1'
          ? parseInt(data.event_id, 10)
          : undefined
      );
    },
    [handleLoadEvents]
  );

  const handleChangeUnlisted = useCallback(
    async (event) => {
      const newEvents = events.map((eventData) => {
        if (eventData.id === event.id) {
          return {
            ...eventData,
            status: event.status,
            unlisted: !eventData.unlisted,
          };
        }
        return eventData;
      });

      setEvents(newEvents);
    },
    [events]
  );

  return (
    <Container className="mt-n3 mb-n3">
      <InfiniteScroll
        id="infinite-scroll"
        onInfiniteLoad={handleLoad}
        className="infinite-scroll py-3"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="zoom col-12 px-0">
              <div className="bg-white d-flex justify-content-between align-items-center p-4">
                <h1 className="mb-0">Eventos</h1>
                <div>
                  <BtnNewEvent />
                </div>
              </div>
            </div>
            <Filter className="col-12 px-0 mt-3 d-flex justify-content-between align-items-end">
              <FiltersLists
                onSubmitFilters={handleSubmitFilters}
                category
                statusEvent
                date
                categories={categories}
                statusOptions={status}
                event
              />
              <p className="h6 mb-0 small">Total: {totalEvents}</p>
            </Filter>

            <div className="zoom col-12 pe-0">
              {events.map((event) => (
                <Card
                  key={event.id}
                  event={event}
                  onChangeUnlisted={handleChangeUnlisted}
                  onDeleteEvent={handleClickDelete}
                  onCopyEvent={handleCopy}
                />
              ))}
            </div>
          </div>
        </div>
      </InfiniteScroll>

      <Loading active={loadingMore} text="Carregando..." />
    </Container>
  );
};

export default Events;
